export const AsyncStatus = {
  INITIAL: 'initial',
  LOADING: 'loading',
  SUCCESS: 'success',
  FAILURE: 'failure'
} as const;

export type AsyncStatus = (typeof AsyncStatus)[keyof typeof AsyncStatus];

export class AsyncAction<T = void, E = unknown> {
  readonly status: AsyncStatus;
  readonly data: T | null;
  readonly error: E | null;

  private constructor(status: AsyncStatus, data: T | null, error: E | null) {
    this.status = status;
    this.data = data;
    this.error = error;
  }

  // Factory methods
  static initial<T = void, E = unknown>(): AsyncAction<T, E> {
    return new AsyncAction<T, E>(AsyncStatus.INITIAL, null, null);
  }

  static loading<T = void, E = unknown>(): AsyncAction<T, E> {
    return new AsyncAction<T, E>(AsyncStatus.LOADING, null, null);
  }

  static success<T = void, E = unknown>(data?: T): AsyncAction<T, E> {
    return new AsyncAction<T, E>(AsyncStatus.SUCCESS, data ?? null, null);
  }

  static failure<T = void, E = unknown>(error: E): AsyncAction<T, E> {
    return new AsyncAction<T, E>(AsyncStatus.FAILURE, null, error);
  }

  // Status checkers
  get isInitial(): boolean {
    return this.status === AsyncStatus.INITIAL;
  }

  get isLoading(): boolean {
    return this.status === AsyncStatus.LOADING;
  }

  get isSuccess(): boolean {
    return this.status === AsyncStatus.SUCCESS;
  }

  get isFailure(): boolean {
    return this.status === AsyncStatus.FAILURE;
  }
}
